import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Boxes,
  LogOut,
  Plus,
  Search,
  X,
  Pencil,
  Trash2,
  Users,
  BarChart3,
  KeyRound,
  ImagePlus,
  FileSpreadsheet,
  Download,
  Images,
  Wrench,
  ChevronDown,
  ChevronUp,
  CalendarCheck,
  History,
  Eraser,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
const API = process.env.REACT_APP_BACKEND_URL,
  hdr = () => ({
    Authorization: `Bearer ${localStorage.getItem("e93_inventory_admin_token")}`,
  });
const variant = () => ({
  id: "",
  model: "Standard",
  selling_price: "",
  stock: "0",
  low_stock_threshold: "2",
});
const empty = () => ({
  name: "",
  sku: "",
  category: "Telefona",
  brand: "",
  image_url: "",
  notes: "",
  variants: [variant()],
});
const money = (v) =>
    `${Math.round((v || 0) / 100).toLocaleString("sq-AL")} Lek`,
  img = (u) => (u ? (u.startsWith("http") ? u : `${API}${u}`) : "");
const saleQuantityText = (sale) => {
  const quantity = Number(sale.quantity || 0);
  const packages = Number(sale.packages || 0);
  const piecesPerPack = Number(sale.pieces_per_pack || 20);

  if (sale.sale_type === "pack" || packages > 0) {
    const packageCount =
      packages || Math.max(1, Math.floor(quantity / piecesPerPack));
    return `${packageCount} ${packageCount === 1 ? "paketë" : "paketa"}`;
  }

  if (sale.sale_type === "loose") {
    return `${quantity} ${quantity === 1 ? "cigare e hapur" : "cigare të hapura"}`;
  }

  const tobacco = norm(sale.category).includes("tabakino");
  if (tobacco) {
    return `${quantity} ${quantity === 1 ? "paketë" : "paketa"}`;
  }

  return `${quantity} ${quantity === 1 ? "copë" : "copë"}`;
};
const isCigarette = (product) =>
  (product.name || "").toLowerCase().includes("cigare");
const stockText = (product, item) => {
  const tracked = item.track_by_pack ?? product.track_by_pack;
  if (!tracked) {
    const stock = Number(item.stock || 0);
    return isCigarette(product)
      ? `${stock} ${stock === 1 ? "paketë" : "paketa"}`
      : `${stock} copë`;
  }
  const perPack = Number(item.pieces_per_pack || product.pieces_per_pack || 20);
  const stock = Number(item.stock || 0);
  const packs = Math.floor(stock / perPack);
  const pieces = stock % perPack;
  if (!pieces) return `${packs} ${packs === 1 ? "paketë" : "paketa"}`;
  if (!packs) return `${pieces} cigare`;
  return `${packs} ${packs === 1 ? "paketë" : "paketa"} dhe ${pieces} cigare`;
};
const thresholdText = (product, item) => {
  const tracked = item.track_by_pack ?? product.track_by_pack;
  const threshold = Number(item.low_stock_threshold || 0);
  if (tracked) {
    const perPack = Number(item.pieces_per_pack || product.pieces_per_pack || 20);
    const packs = Math.ceil(threshold / perPack);
    return `${packs} ${packs === 1 ? "paketë" : "paketa"}`;
  }
  return isCigarette(product)
    ? `${threshold} ${threshold === 1 ? "paketë" : "paketa"}`
    : `${threshold} copë`;
};
const PAGE_SIZE = 50;
const dayKey = (value) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Tirane",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(value));
const periodMonthLabel = (value) =>
  value
    ? new Date(`${value}-01T12:00:00`).toLocaleDateString("sq-AL", {
        month: "long",
        year: "numeric",
      })
    : "—";
const norm = (v) =>
  (v || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
const distance = (a, b) => {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++)
      d[i][j] = Math.min(
        d[i - 1][j] + 1,
        d[i][j - 1] + 1,
        d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
  return d[a.length][b.length];
};
const wordMatch = (token, text) =>
  text.includes(token) ||
  (token.length >= 5 &&
    text
      .split(/\s+/)
      .some(
        (word) =>
          Math.abs(word.length - token.length) <= 2 &&
          distance(token, word) <= 2,
      ));
const matchingVariants = (p, query) => {
  const tokens = norm(query).split(/\s+/).filter(Boolean);
  if (!tokens.length) return p.variants || [];
  return (p.variants || []).filter((v) => {
    const tracked = v.track_by_pack ?? p.track_by_pack;
    const text = norm(
      [
        p.name,
        p.sku,
        p.brand,
        p.category,
        v.model,
        tracked ? "pakete paketë e hapur cope copë" : "",
      ].join(" "),
    );
    return tokens.every((t) => wordMatch(t, text));
  });
};
export default function Inventory() {
  const nav = useNavigate(),
    [products, setProducts] = useState([]),
    [sales, setSales] = useState([]),
    [services, setServices] = useState([]),
    [periodHistory, setPeriodHistory] = useState([]),
    [servicePeriod, setServicePeriod] = useState({
      active: false,
      started_at: null,
      closed_at: null,
    }),
    [users, setUsers] = useState([]),
    [search, setSearch] = useState(""),
    [productFilter, setProductFilter] = useState("all"),
    [productPage, setProductPage] = useState(1),
    [saleFilter, setSaleFilter] = useState("all"),
    [historyYear, setHistoryYear] = useState(String(new Date().getFullYear())),
    [startMonth, setStartMonth] = useState(dayKey(new Date()).slice(0, 7)),
    [expanded, setExpanded] = useState({}),
    [lowStockOpen, setLowStockOpen] = useState(false),
    [open, setOpen] = useState(false),
    [editing, setEditing] = useState(null),
    [form, setForm] = useState(empty()),
    [userOpen, setUserOpen] = useState(false),
    [editingUser, setEditingUser] = useState(null),
    [userForm, setUserForm] = useState({
      username: "",
      password: "",
      active: true,
    }),
    [credOpen, setCredOpen] = useState(false),
    [creds, setCreds] = useState({
      current_password: "",
      new_username: "",
      new_password: "",
    });
  const logout = useCallback(() => {
    localStorage.removeItem("e93_inventory_admin_token");
    nav("/inventory/admin/login");
  }, [nav]);
  const load = useCallback(async () => {
    try {
      let [p, s, sv, sp, ph, u] = await Promise.all([
        axios.get(`${API}/api/inventory/products`, { headers: hdr() }),
        axios.get(`${API}/api/inventory/sales`, { headers: hdr() }),
        axios.get(`${API}/api/inventory/services`, { headers: hdr() }),
        axios.get(`${API}/api/inventory/services/period`, { headers: hdr() }),
        axios.get(`${API}/api/inventory/services/periods`, { headers: hdr() }),
        axios.get(`${API}/api/inventory/users`, { headers: hdr() }),
      ]);
      setProducts(p.data);
      setSales(s.data);
      setServices(sv.data);
      setServicePeriod(sp.data);
      setPeriodHistory(ph.data);
      setUsers(u.data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) logout();
      else toast.error("Të dhënat nuk u ngarkuan");
    }
  }, [logout]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const refresh = () => load();
    const onVisibility = () => {
      if (document.visibilityState === "visible") load();
    };
    const timer = window.setInterval(refresh, 30000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);
  const shown = useMemo(
    () =>
      products
        .filter(
          (p) =>
            productFilter === "all" ||
            (productFilter === "phone" &&
              ["Telefona", "Aksesorë telefonash"].includes(p.category)) ||
            (productFilter === "tobacco" &&
              p.category === "Produkte Tabakino") ||
            (productFilter === "other" &&
              ![
                "Telefona",
                "Aksesorë telefonash",
                "Produkte Tabakino",
              ].includes(p.category)),
        )
        .filter((p) => matchingVariants(p, search).length > 0),
    [products, search, productFilter],
  );
  const productPages = Math.max(1, Math.ceil(shown.length / PAGE_SIZE)),
    pagedProducts = shown.slice(
      (Math.min(productPage, productPages) - 1) * PAGE_SIZE,
      Math.min(productPage, productPages) * PAGE_SIZE,
    );
  const today = dayKey(new Date()),
    afterDailyReset = (row) =>
      !servicePeriod.daily_reset_at ||
      row.created_at >= servicePeriod.daily_reset_at,
    daily = sales.filter(
      (s) => dayKey(s.created_at) === today && afterDailyReset(s),
    ),
    dailyServices = services.filter(
      (s) => dayKey(s.created_at) === today && afterDailyReset(s),
    ),
    insidePeriod = (row) =>
      Boolean(servicePeriod.started_at) &&
      row.created_at >= servicePeriod.started_at &&
      (!servicePeriod.closed_at || row.created_at <= servicePeriod.closed_at),
    monthly = sales.filter(insidePeriod),
    monthlyServices = services.filter(insidePeriod),
    units = products.reduce(
      (a, p) => a + (p.variants || []).reduce((b, v) => b + v.stock, 0),
      0,
    ),
    low = products.reduce(
      (a, p) =>
        a +
        (p.variants || []).filter((v) => v.stock <= v.low_stock_threshold)
          .length,
      0,
    );
  const categories = [
    "Telefona",
    "Aksesorë telefonash",
    "Produkte Tabakino",
    "Të tjera",
  ];
  const revenue = (rows, category) =>
    rows
      .filter((s) => (s.category || "Të tjera") === category)
      .reduce((a, s) => a + s.total, 0);
  const serviceOwner = (rows) => rows.reduce((a, s) => a + s.owner_share, 0),
    serviceFull = (rows) =>
      rows.reduce((a, s) => a + (s.net_profit ?? s.amount), 0),
    saleTotal = (rows) => rows.reduce((a, s) => a + s.total, 0);
  const lowStockGroups = categories
    .map((category) => ({
      category,
      rows: products.flatMap((p) =>
        (p.variants || [])
          .filter((v) => v.stock <= v.low_stock_threshold)
          .filter(() => (p.category || "Të tjera") === category)
          .map((v) => ({ product: p, variant: v })),
      ),
    }))
    .filter((group) => group.rows.length);
  const monthlyServiceValue = servicePeriod.active
    ? serviceFull(monthlyServices)
    : serviceOwner(monthlyServices);
  const isPhone = (s) =>
      ["Telefona", "Aksesorë telefonash"].includes(s.category),
    isTobacco = (s) => s.category === "Produkte Tabakino";
  const filteredSales = sales.filter(
    (s) =>
      saleFilter === "all" ||
      (saleFilter === "phone" && isPhone(s)) ||
      (saleFilter === "tobacco" && isTobacco(s)) ||
      (saleFilter === "other" && !isPhone(s) && !isTobacco(s)),
  );
  const historyYears = Array.from(
    new Set([
      String(new Date().getFullYear()),
      ...periodHistory.map((p) =>
        String((p.month_key || p.closed_at || "").slice(0, 4)),
      ),
    ]),
  )
    .filter((x) => /^\d{4}$/.test(x))
    .sort((a, b) => b.localeCompare(a));
  const monthNames = [
    "Janar",
    "Shkurt",
    "Mars",
    "Prill",
    "Maj",
    "Qershor",
    "Korrik",
    "Gusht",
    "Shtator",
    "Tetor",
    "Nëntor",
    "Dhjetor",
  ];
  const yearlyMonths = monthNames.map((name, index) => {
    const key = `${historyYear}-${String(index + 1).padStart(2, "0")}`;
    const rows = periodHistory.filter(
      (p) => (p.month_key || (p.closed_at || "").slice(0, 7)) === key,
    );
    const productSales = rows.reduce(
      (a, p) => a + Number(p.product_sales_total || 0),
      0,
    );
    const serviceTotal = rows.reduce(
      (a, p) => a + Number(p.net_profit_total ?? p.total_amount ?? 0),
      0,
    );
    const sellerPayout = rows.reduce(
      (a, p) => a + Number(p.seller_share || 0),
      0,
    );
    const ownerService = rows.reduce(
      (a, p) => a + Number(p.owner_share || 0),
      0,
    );
    return {
      key,
      name,
      closures: rows.length,
      productSales,
      serviceTotal,
      sellerPayout,
      ownerService,
      realTurnover: productSales + ownerService,
    };
  });
  const annualRealTurnover = yearlyMonths.reduce(
    (a, row) => a + row.realTurnover,
    0,
  );
  const showProduct = (p) => {
    setEditing(p || null);
    setForm(
      p
        ? {
            ...p,
            variants: (p.variants || [variant()]).map((v) => {
              const tracked = v.track_by_pack ?? p.track_by_pack;
              const perPack = Number(v.pieces_per_pack || p.pieces_per_pack || 20);
              return {
                ...v,
                selling_price: String(Math.round((v.selling_price || 0) / 100)),
                stock: String(v.stock || 0),
                low_stock_threshold: String(v.low_stock_threshold || 2),
                stock_packages: tracked ? String(Math.floor(Number(v.stock || 0) / perPack)) : "",
                stock_loose: tracked ? String(Number(v.stock || 0) % perPack) : "",
                low_stock_packages: tracked ? String(Math.ceil(Number(v.low_stock_threshold || 0) / perPack)) : "",
              };
            }),
          }
        : empty(),
    );
    setOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v })),
    setV = (i, k, v) =>
      setForm((f) => ({
        ...f,
        variants: f.variants.map((x, j) => (j === i ? { ...x, [k]: v } : x)),
      }));
  const upload = async (e) => {
    let f = e.target.files?.[0];
    if (!f) return;
    let body = new FormData();
    body.append("file", f);
    try {
      let { data } = await axios.post(`${API}/api/inventory/upload`, body, {
        headers: { ...hdr(), "Content-Type": "multipart/form-data" },
      });
      set("image_url", data.url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ngarkimi dështoi");
    }
  };
  const save = async (e) => {
    e.preventDefault();
    let payload = {
      ...form,
      variants: form.variants.map((v) => {
        const tracked = v.track_by_pack ?? form.track_by_pack;
        const perPack = Number(v.pieces_per_pack || form.pieces_per_pack || 20);
        return {
          ...v,
          selling_price: Math.round(Number(v.selling_price || 0) * 100),
          stock: tracked
            ? Number(v.stock_packages || 0) * perPack + Number(v.stock_loose || 0)
            : Number(v.stock || 0),
          low_stock_threshold: tracked
            ? Number(v.low_stock_packages || 0) * perPack
            : Number(v.low_stock_threshold || 0),
        };
      }),
    };
    try {
      editing
        ? await axios.put(
            `${API}/api/inventory/products/${editing.id}`,
            payload,
            { headers: hdr() },
          )
        : await axios.post(`${API}/api/inventory/products`, payload, {
            headers: hdr(),
          });
      toast.success("Produkti u ruajt");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ruajtja dështoi");
    }
  };
  const remove = async (p) => {
    if (window.confirm(`Të fshihet ${p.name}?`)) {
      await axios.delete(`${API}/api/inventory/products/${p.id}`, {
        headers: hdr(),
      });
      load();
    }
  };
  const showUser = (u) => {
    setEditingUser(u || null);
    setUserForm(
      u
        ? { username: u.username, password: "", active: u.active }
        : { username: "", password: "", active: true },
    );
    setUserOpen(true);
  };
  const saveUser = async (e) => {
    e.preventDefault();
    try {
      editingUser
        ? await axios.put(
            `${API}/api/inventory/users/${editingUser.id}`,
            userForm,
            { headers: hdr() },
          )
        : await axios.post(`${API}/api/inventory/users`, userForm, {
            headers: hdr(),
          });
      toast.success("Shitësi u ruajt");
      setUserOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ruajtja dështoi");
    }
  };
  const changeCreds = async (e) => {
    e.preventDefault();
    try {
      let { data } = await axios.post(
        `${API}/api/inventory/auth/change-credentials`,
        creds,
        { headers: hdr() },
      );
      localStorage.setItem("e93_inventory_admin_token", data.token);
      toast.success("Kredencialet u ndryshuan");
      setCredOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ndryshimi dështoi");
    }
  };
  const downloadFile = async (path, name) => {
    try {
      let { data } = await axios.get(`${API}${path}`, {
        headers: hdr(),
        responseType: "blob",
      });
      let url = URL.createObjectURL(data),
        a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Shkarkimi dështoi");
    }
  };
  const importExcel = async (e) => {
    let file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    let body = new FormData();
    body.append("file", file);
    try {
      let { data } = await axios.post(
        `${API}/api/inventory/products/import/excel`,
        body,
        { headers: { ...hdr(), "Content-Type": "multipart/form-data" } },
      );
      toast.success(
        `Importi përfundoi: ${data.created} të rinj, ${data.updated} të përditësuar`,
      );
      load();
    } catch (e) {
      let d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Importi dështoi");
    }
  };
  const importPhotos = async (e) => {
    let file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    let body = new FormData();
    body.append("file", file);
    try {
      let { data } = await axios.post(
        `${API}/api/inventory/products/import/photos`,
        body,
        { headers: { ...hdr(), "Content-Type": "multipart/form-data" } },
      );
      toast.success(
        `${data.updated} foto u lidhën; ${data.skipped} u anashkaluan`,
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Importi i fotove dështoi");
    }
  };
  const closePeriod = async () => {
    if (
      !window.confirm(
        "Të mbyllet periudha e serviseve? Totali i shitësve do të nisë nga zero, por historiku nuk do të fshihet.",
      )
    )
      return;
    try {
      const { data } = await axios.post(
        `${API}/api/inventory/services/close-period`,
        {},
        { headers: hdr() },
      );
      const breakdown = (data.seller_payouts || [])
        .map((x) => `${x.seller_username}: ${money(x.amount)}`)
        .join("\n");
      window.alert(
        `Periudha ${periodMonthLabel(data.month_key)} u mbyll.\n\nPër serviset duhet t'u paguash shitësve gjithsej: ${money(data.seller_share)}${breakdown ? `\n\nNdarja sipas shitësit:\n${breakdown}` : ""}\n\nXhiroja reale e periudhës: ${money(data.real_turnover)}`,
      );
      toast.success("Periudha u ruajt në historikun vjetor");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Periudha nuk u mbyll");
    }
  };
  const startPeriod = async () => {
    if (
      !window.confirm(
        "Të fillojë një periudhë e re tani? Xhiroja e sotme dhe të gjitha llogaritjet e periudhës së re do të nisin nga zero. Historiku do të ruhet.",
      )
    )
      return;
    try {
      await axios.post(
        `${API}/api/inventory/services/start-period`,
        { period_month: startMonth },
        { headers: hdr() },
      );
      toast.success(
        `Periudha ${startMonth} dhe xhiroja e sotme filluan nga zero`,
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Periudha nuk u hap");
    }
  };
  const clearSales = async () => {
    if (
      !window.confirm(
        "KUJDES: Të fshihen TË GJITHA shitjet? Sasitë e shitura do të rikthehen automatikisht në stok.",
      )
    )
      return;
    try {
      let { data } = await axios.delete(`${API}/api/inventory/sales/all`, {
        headers: hdr(),
      });
      toast.success(
        `${data.deleted} shitje u fshinë dhe ${data.restored_stock} copë u rikthyen në stok`,
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Shitjet nuk u fshinë");
    }
  };
  const clearServices = async () => {
    if (
      !window.confirm(
        "KUJDES: Të fshihen TË GJITHA serviset dhe periudhat e mbyllura? Ky veprim pastron të gjitha provat.",
      )
    )
      return;
    try {
      let { data } = await axios.delete(`${API}/api/inventory/services/all`, {
        headers: hdr(),
      });
      toast.success(`${data.deleted} servise u fshinë; periudha nisi nga zero`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Serviset nuk u fshinë");
    }
  };
  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-slate-950 text-white p-3 sm:p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 gap-3 items-center">
          <img
            src="/e93-logo.jpg"
            alt="E93"
            className="w-11 h-11 rounded-full object-cover"
          />
          <div className="min-w-0">
            <b className="block text-sm sm:text-base">Administrimi i Inventarit E93</b>
            <p className="text-xs text-slate-400">
              Telefona • Aksesorë • Produkte Tabakino
            </p>
          </div>
        </div>
        <div className="flex w-full gap-2 sm:w-auto">
          <Button
            variant="outline"
            onClick={() => setCredOpen(true)}
            className="flex-1 bg-transparent text-white sm:flex-none"
          >
            <KeyRound className="h-4 w-4 mr-1" />
            Kredencialet
          </Button>
          <Button
            variant="outline"
            onClick={logout}
            className="bg-transparent text-white"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto p-2.5 sm:p-4 md:p-7">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 mb-5">
          {[
            ["Copë aktualisht në stok", units],
            ["Produkte me stok të ulët", low],
            [
              "Totali i xhiros sot",
              money(saleTotal(daily) + serviceFull(dailyServices)),
            ],
            [
              "Totali i xhiros së periudhës",
              money(saleTotal(monthly) + monthlyServiceValue),
            ],
          ].map((x) => (
            <Card
              key={x[0]}
              role={x[0] === "Produkte me stok të ulët" ? "button" : undefined}
              tabIndex={x[0] === "Produkte me stok të ulët" ? 0 : undefined}
              onClick={x[0] === "Produkte me stok të ulët" ? () => setLowStockOpen(true) : undefined}
              onKeyDown={x[0] === "Produkte me stok të ulët" ? (e) => { if (e.key === "Enter") setLowStockOpen(true); } : undefined}
              className={x[0] === "Produkte me stok të ulët" ? "cursor-pointer transition hover:border-red-300 hover:shadow-md" : ""}
            >
              <CardContent className="p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-gray-500">{x[0]}</p>
                <b className="block break-words text-lg sm:text-2xl">{x[1]}</b>
                {x[0] === "Produkte me stok të ulët" && (
                  <p className="mt-1 text-xs font-medium text-red-600">Kliko për listën e furnizimit</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-5">
          {[
            ["Ndarja e sotme", daily, dailyServices, false],
            ["Ndarja e periudhës", monthly, monthlyServices, true],
          ].map(([title, rows, serviceRows, isPeriod]) => (
            <Card key={title}>
              <CardContent className="p-4">
                <b>{title}</b>
                <div className="mt-3 space-y-1 text-sm">
                  {categories.map((c) => (
                    <div className="flex justify-between" key={c}>
                      <span>{c} (të ardhura)</span>
                      <b>{money(revenue(rows, c))}</b>
                    </div>
                  ))}
                  <div className="flex justify-between border-t pt-2">
                    <span>
                      {isPeriod && !servicePeriod.active
                        ? "Servise — pjesa e pronarit pas mbylljes (50%)"
                        : "Servise — fitimi pas kostove"}
                    </span>
                    <b>
                      {money(
                        isPeriod
                          ? servicePeriod.active
                            ? serviceFull(serviceRows)
                            : serviceOwner(serviceRows)
                          : serviceFull(serviceRows),
                      )}
                    </b>
                  </div>
                  <div className="flex justify-between font-bold text-base">
                    <span>Totali i përgjithshëm</span>
                    <span>
                      {money(
                        saleTotal(rows) +
                          (isPeriod
                            ? servicePeriod.active
                              ? serviceFull(serviceRows)
                              : serviceOwner(serviceRows)
                            : serviceFull(serviceRows)),
                      )}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <Tabs defaultValue="products">
          <TabsList className="flex h-auto w-full justify-start overflow-x-auto p-1">
            <TabsTrigger value="products" className="shrink-0 text-xs sm:text-sm">
              <Boxes className="h-4 w-4 mr-1" />
              Produktet
            </TabsTrigger>
            <TabsTrigger value="sales" className="shrink-0 text-xs sm:text-sm">
              <BarChart3 className="h-4 w-4 mr-1" />
              Shitjet
            </TabsTrigger>
            <TabsTrigger value="services" className="shrink-0 text-xs sm:text-sm">
              <Wrench className="h-4 w-4 mr-1" />
              Serviset
            </TabsTrigger>
            <TabsTrigger value="history" className="shrink-0 text-xs sm:text-sm">
              <History className="h-4 w-4 mr-1" />
              Xhiroja vjetore
            </TabsTrigger>
            <TabsTrigger value="users" className="shrink-0 text-xs sm:text-sm">
              <Users className="h-4 w-4 mr-1" />
              Shitësit
            </TabsTrigger>
          </TabsList>
          <TabsContent value="products">
            <div className="flex flex-col gap-3 my-4 lg:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4" />
                <Input
                  className="pl-9 pr-10 bg-white"
                  placeholder="Kërko produkt ose model..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setProductPage(1);
                  }}
                />
                {search && (
                  <button
                    type="button"
                    aria-label="Pastro kërkimin"
                    className="absolute right-3 top-2.5 text-gray-500 hover:text-black"
                    onClick={() => {
                      setSearch("");
                      setProductPage(1);
                    }}
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                <Button className="col-span-2 sm:col-span-1" 
                  variant="outline"
                  onClick={() =>
                    downloadFile(
                      "/api/inventory/products/import/template",
                      "Modeli_Importit_Inventar_E93.xlsx",
                    )
                  }
                >
                  <Download className="h-4 w-4 mr-1" />
                  Modeli Excel
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    downloadFile(
                      "/api/inventory/products/export/excel",
                      "Inventari_E93.xlsx",
                    )
                  }
                >
                  <FileSpreadsheet className="h-4 w-4 mr-1" />
                  Export Excel
                </Button>
                <label className="inline-flex items-center border px-3 py-2 text-sm cursor-pointer">
                  <FileSpreadsheet className="h-4 w-4 mr-1" />
                  Import Excel
                  <input
                    type="file"
                    accept=".xlsx"
                    className="hidden"
                    onChange={importExcel}
                  />
                </label>
                <label className="inline-flex items-center border px-3 py-2 text-sm cursor-pointer">
                  <Images className="h-4 w-4 mr-1" />
                  ZIP Foto
                  <input
                    type="file"
                    accept=".zip"
                    className="hidden"
                    onChange={importPhotos}
                  />
                </label>
                <Button onClick={() => showProduct()}>
                  <Plus className="h-4 w-4 mr-1" />
                  Shto
                </Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                ["all", "Të gjitha produktet"],
                ["phone", "Telefonia"],
                ["tobacco", "Produkte Tabakino"],
                ["other", "Të tjera"],
              ].map(([key, label]) => (
                <Button
                  key={key}
                  variant={productFilter === key ? "default" : "outline"}
                  onClick={() => {
                    setProductFilter(key);
                    setProductPage(1);
                  }}
                >
                  {label}
                </Button>
              ))}
            </div>
            <p className="text-sm text-gray-500 mb-3">
              Po shfaqen{" "}
              {shown.length
                ? (Math.min(productPage, productPages) - 1) * PAGE_SIZE + 1
                : 0}
              –
              {Math.min(
                Math.min(productPage, productPages) * PAGE_SIZE,
                shown.length,
              )}{" "}
              nga {shown.length} produkte
            </p>
            <div className="space-y-3">
              {pagedProducts.map((p) => {
                const visible = matchingVariants(p, search);
                const isOpen = Boolean(search.trim()) || expanded[p.id];
                return (
                  <Card key={p.id}>
                    <CardContent className="p-0">
                      <div className="p-3 sm:p-4 flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                        <div className="flex min-w-0 gap-3 items-center">
                          {p.image_url && (
                            <img
                              className="w-14 h-14 rounded-md object-cover"
                              src={img(p.image_url)}
                              alt=""
                            />
                          )}
                          <div className="min-w-0">
                            <b className="block break-words text-base">{p.name}</b>
                            <p className="break-words text-xs text-gray-500 sm:text-sm">
                              {p.brand} • {p.category} • {p.sku}
                            </p>
                            <p className="text-sm mt-1">
                              <b>{visible.length}</b>{" "}
                              {search ? "rezultate" : "modele"} •{" "}
                              <b>
                                {visible.length === 1
                                  ? stockText(p, visible[0])
                                  : isCigarette(p)
                                    ? `${visible.length} modele cigare`
                                    : visible.every(
                                          (v) => v.track_by_pack ?? p.track_by_pack,
                                        )
                                    ? `${visible.reduce((a, v) => a + Math.floor(Number(v.stock || 0) / Number(v.pieces_per_pack || p.pieces_per_pack || 20)), 0)} paketa gjithsej`
                                    : `${visible.reduce((a, v) => a + v.stock, 0)} copë gjithsej`}
                              </b>
                            </p>
                          </div>
                        </div>
                        <div className="flex w-full gap-2 sm:w-auto">
                          <Button
                            variant="outline"
                            className="min-w-0 flex-1 sm:flex-none"
                            onClick={() =>
                              setExpanded((x) => ({ ...x, [p.id]: !isOpen }))
                            }
                          >
                            {isOpen ? (
                              <ChevronUp className="h-4 w-4 mr-1" />
                            ) : (
                              <ChevronDown className="h-4 w-4 mr-1" />
                            )}
                            {isOpen
                              ? "Mbyll modelet"
                              : `Shfaq modelet (${visible.length})`}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => showProduct(p)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => remove(p)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {isOpen && (
                        <div className="border-t overflow-x-auto">
                          <table className="w-full min-w-[620px] text-sm">
                            <thead>
                              <tr className="text-left bg-slate-50">
                                <th className="p-3 w-2/5">Modeli / Varianti</th>
                                <th className="p-3">Çmimi i shitjes</th>
                                <th className="p-3">Sasia në stok</th>
                                <th className="p-3">Gjendja</th>
                              </tr>
                            </thead>
                            <tbody>
                              {visible.map((v) => (
                                <tr className="border-t" key={v.id}>
                                  <td className="p-3 font-medium">{v.model}</td>
                                  <td className="p-3">
                                    {money(v.selling_price)}
                                  </td>
                                  <td className="p-3">{stockText(p, v)}</td>
                                  <td className="p-3">
                                    <span
                                      className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${v.stock <= v.low_stock_threshold ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}
                                    >
                                      {v.stock <= v.low_stock_threshold
                                        ? "Stok i ulët"
                                        : "Në gjendje"}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <Pagination
              page={Math.min(productPage, productPages)}
              pages={productPages}
              onChange={setProductPage}
            />
          </TabsContent>
          <TabsContent value="sales">
            <div className="grid md:grid-cols-2 gap-4 my-4">
              {[
                ["Sot", daily],
                ["Këtë muaj", monthly],
              ].map(([label, rows]) => (
                <Card key={label}>
                  <CardContent className="p-4">
                    <b>{label}</b>
                    <div className="mt-3 space-y-2">
                      <div className="flex justify-between">
                        <span>
                          Telefonia —{" "}
                          {rows
                            .filter(isPhone)
                            .reduce((a, s) => a + s.quantity, 0)}{" "}
                          copë
                        </span>
                        <b>
                          {money(
                            rows
                              .filter(isPhone)
                              .reduce((a, s) => a + s.total, 0),
                          )}
                        </b>
                      </div>
                      <div className="flex justify-between">
                        <span>
                          Produkte Tabakino —{" "}
                          {rows
                            .filter(isTobacco)
                            .reduce((a, s) => a + s.quantity, 0)}{" "}
                          copë
                        </span>
                        <b>
                          {money(
                            rows
                              .filter(isTobacco)
                              .reduce((a, s) => a + s.total, 0),
                          )}
                        </b>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span>Të gjitha shitjet</span>
                        <b>{money(saleTotal(rows))}</b>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="my-4 flex flex-wrap justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                {[
                  ["all", "Të gjitha"],
                  ["phone", "Telefonia"],
                  ["tobacco", "Produkte Tabakino"],
                  ["other", "Të tjera"],
                ].map(([key, label]) => (
                  <Button
                    key={key}
                    variant={saleFilter === key ? "default" : "outline"}
                    onClick={() => setSaleFilter(key)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
              <Button
                variant="destructive"
                onClick={clearSales}
                disabled={!sales.length}
              >
                <Eraser className="h-4 w-4 mr-1" />
                Fshi të gjitha shitjet dhe rikthe stokun
              </Button>
            </div>
            <div className="bg-white border overflow-x-auto">
              <table className="w-full min-w-[620px] text-sm">
                <thead>
                  <tr className="text-left bg-slate-50">
                    <th className="p-3">Data</th>
                    <th>Shitësi</th>
                    <th>Produkti</th>
                    <th>Kategoria</th>
                    <th>Sasia</th>
                    <th>Totali</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.map((s) => (
                    <tr className="border-t" key={s.id}>
                      <td className="p-3">
                        {new Date(s.created_at).toLocaleString("sq-AL")}
                      </td>
                      <td>{s.seller_username}</td>
                      <td>
                        {s.product_name} — {s.model}
                      </td>
                      <td>
                        {isPhone(s) ? "Telefonia" : s.category || "Të tjera"}
                      </td>
                      <td className="font-medium">{saleQuantityText(s)}</td>
                      <td>{money(s.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
          <TabsContent value="services">
            <div className="my-4 flex flex-wrap justify-between items-center gap-3 rounded-md border bg-white p-4">
              <div>
                <b>
                  {servicePeriod.active
                    ? "Periudha aktive"
                    : "Periudha është e mbyllur"}
                </b>
                <p className="text-sm text-gray-500">
                  Muaji i raportimit:{" "}
                  <b>{periodMonthLabel(servicePeriod.period_month)}</b>.{" "}
                  {servicePeriod.active ? "Ka nisur" : "Periudha e fundit nisi"}{" "}
                  më{" "}
                  {servicePeriod.started_at
                    ? new Date(servicePeriod.started_at).toLocaleString("sq-AL")
                    : "—"}
                  {servicePeriod.closed_at
                    ? ` dhe u mbyll më ${new Date(servicePeriod.closed_at).toLocaleString("sq-AL")}.`
                    : "."}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {servicePeriod.active ? (
                  <Button variant="outline" onClick={closePeriod}>
                    <CalendarCheck className="h-4 w-4 mr-1" />
                    Mbyll periudhën
                  </Button>
                ) : (
                  <div className="flex flex-wrap items-end gap-2">
                    <div>
                      <Label>Muaji që përfaqëson periudha</Label>
                      <Input
                        className="mt-1 bg-white"
                        type="month"
                        value={startMonth}
                        onChange={(e) => setStartMonth(e.target.value)}
                      />
                    </div>
                    <Button onClick={startPeriod} disabled={!startMonth}>
                      <CalendarCheck className="h-4 w-4 mr-1" />
                      Fillo periudhën e re
                    </Button>
                  </div>
                )}
                <Button
                  variant="destructive"
                  onClick={clearServices}
                  disabled={!services.length}
                >
                  <Eraser className="h-4 w-4 mr-1" />
                  Fshi të gjitha serviset
                </Button>
              </div>
            </div>
            <div className="bg-white border overflow-x-auto">
              <table className="w-full min-w-[620px] text-sm">
                <thead>
                  <tr className="text-left bg-slate-50">
                    <th className="p-3">Data</th>
                    <th>Shitësi</th>
                    <th>Servisi</th>
                    <th>Pajisja</th>
                    <th>Vlera e servisit</th>
                    <th>Kosto pjesë</th>
                    <th>Transport</th>
                    <th>Fitimi pas kostove</th>
                    <th>Shitësi 50%</th>
                    <th>Pronari 50%</th>
                    <th>Shënime</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((s) => (
                    <tr className="border-t" key={s.id}>
                      <td className="p-3">
                        {new Date(s.created_at).toLocaleString("sq-AL")}
                      </td>
                      <td>{s.seller_username}</td>
                      <td>{s.service_type}</td>
                      <td>{s.device_model || "—"}</td>
                      <td>{money(s.amount)}</td>
                      <td>{money(s.product_cost || 0)}</td>
                      <td>{money(s.transport_cost || 0)}</td>
                      <td className="font-semibold">{money(s.net_profit ?? s.amount)}</td>
                      <td>{money(s.seller_share)}</td>
                      <td>{money(s.owner_share)}</td>
                      <td>{s.description || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
          <TabsContent value="history">
            <div className="my-4 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold">Xhiroja reale vjetore</h2>
                <p className="text-sm text-gray-500">
                  Shitjet e produkteve 100% + pjesa 50% e pronarit nga serviset
                  e periudhave të mbyllura.
                </p>
              </div>
              <select
                className="h-10 rounded-md border bg-white px-3"
                value={historyYear}
                onChange={(e) => setHistoryYear(e.target.value)}
              >
                {historyYears.map((year) => (
                  <option key={year}>{year}</option>
                ))}
              </select>
            </div>
            <Card className="mb-4 bg-slate-950 text-white">
              <CardContent className="p-5">
                <p className="text-sm text-slate-300">
                  Xhiroja reale totale për {historyYear}
                </p>
                <b className="text-3xl">{money(annualRealTurnover)}</b>
              </CardContent>
            </Card>
            <div className="overflow-x-auto rounded-md border bg-white">
              <table className="w-full min-w-[620px] text-sm">
                <thead>
                  <tr className="bg-slate-50 text-left">
                    <th className="p-3">Muaji</th>
                    <th>Mbyllje</th>
                    <th>Shitje produktesh</th>
                    <th>Fitimi i serviseve pas kostove</th>
                    <th>Për t'u paguar shitësve 50%</th>
                    <th>Pjesa e pronarit 50%</th>
                    <th>Xhiroja reale</th>
                  </tr>
                </thead>
                <tbody>
                  {yearlyMonths.map((row) => (
                    <tr className="border-t" key={row.key}>
                      <td className="p-3 font-semibold">{row.name}</td>
                      <td>{row.closures}</td>
                      <td>{money(row.productSales)}</td>
                      <td>{money(row.serviceTotal)}</td>
                      <td>{money(row.sellerPayout)}</td>
                      <td>{money(row.ownerService)}</td>
                      <td className="font-bold text-emerald-700">
                        {money(row.realTurnover)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-xs text-gray-500">
              Muaji regjistrohet vetëm pasi mbyllet periudha. Mbylljet e vjetra
              që nuk kishin ende ruajtjen e shitjeve mund të tregojnë vetëm
              pjesën e serviseve.
            </p>
          </TabsContent>
          <TabsContent value="users">
            <div className="my-4 text-right">
              <Button onClick={() => showUser()}>
                <Plus className="h-4 w-4 mr-1" />
                Shto shitës
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {users
                .filter((u) => u.role === "seller")
                .map((u) => (
                  <Card key={u.id}>
                    <CardContent className="p-4 flex justify-between">
                      <div>
                        <b>{u.username}</b>
                        <p
                          className={
                            u.active ? "text-emerald-700" : "text-red-600"
                          }
                        >
                          {u.active ? "Aktiv" : "Çaktivizuar"}
                        </p>
                      </div>
                      <Button variant="outline" onClick={() => showUser(u)}>
                        <Pencil className="h-4 w-4 mr-1" />
                        Ndrysho
                      </Button>
                    </CardContent>
                  </Card>
                ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-3xl max-h-[92vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifiko" : "Shto"} produkt</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Produkti *</Label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                />
              </div>
              <div>
                <Label>SKU</Label>
                <Input
                  value={form.sku}
                  onChange={(e) => set("sku", e.target.value)}
                />
              </div>
              <div>
                <Label>Kategoria</Label>
                <select
                  className="w-full border h-10 px-2"
                  value={form.category}
                  onChange={(e) => set("category", e.target.value)}
                >
                  <option>Telefona</option>
                  <option>Aksesorë telefonash</option>
                  <option>Produkte Tabakino</option>
                  <option>Të tjera</option>
                </select>
              </div>
              <div>
                <Label>Marka</Label>
                <Input
                  value={form.brand}
                  onChange={(e) => set("brand", e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label>Foto</Label>
              <label className="border px-3 py-2 inline-flex cursor-pointer">
                <ImagePlus className="h-4 w-4 mr-1" />
                Ngarko
                <input
                  className="hidden"
                  type="file"
                  accept="image/*"
                  onChange={upload}
                />
              </label>
              {form.image_url && (
                <img
                  className="w-20 h-20 object-cover mt-2"
                  src={img(form.image_url)}
                  alt=""
                />
              )}
            </div>
            <Label>Modelet / Variantet</Label>
            <div className="hidden md:grid grid-cols-4 gap-2 px-3 text-xs font-semibold text-gray-600">
              <span>Modeli / Varianti</span>
              <span>Çmimi i shitjes (Lek)</span>
              <span>Sasia aktuale në stok</span>
              <span>Njofto kur stoku bie në</span>
            </div>
            {form.variants.map((v, i) => (
              <div
                key={i}
                className="border p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2"
              >
                <Input
                  required
                  placeholder="Modeli"
                  value={v.model}
                  onChange={(e) => setV(i, "model", e.target.value)}
                />
                <Input
                  type="number"
                  min="0"
                  placeholder="Shitja Lek"
                  value={v.selling_price}
                  onChange={(e) => setV(i, "selling_price", e.target.value)}
                />
                {(v.track_by_pack ?? form.track_by_pack) ? (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="mb-1 block text-xs text-gray-500">Paketa në gjendje</span>
                      <Input
                        type="number"
                        min="0"
                        placeholder="Paketa"
                        value={v.stock_packages}
                        onChange={(e) => setV(i, "stock_packages", e.target.value)}
                      />
                    </div>
                    <div>
                      <span className="mb-1 block text-xs text-gray-500">Cigare të hapura</span>
                      <Input
                        type="number"
                        min="0"
                        max={Number(v.pieces_per_pack || form.pieces_per_pack || 20) - 1}
                        placeholder="Cigare"
                        value={v.stock_loose}
                        onChange={(e) => setV(i, "stock_loose", e.target.value)}
                      />
                    </div>
                  </div>
                ) : (
                  <Input
                    type="number"
                    min="0"
                    placeholder="Stoku"
                    value={v.stock}
                    onChange={(e) => setV(i, "stock", e.target.value)}
                  />
                )}
                <div className="flex">
                  {(v.track_by_pack ?? form.track_by_pack) ? (
                    <div className="flex-1">
                      <span className="mb-1 block text-xs text-gray-500">Pragu në paketa</span>
                      <Input
                        type="number"
                        min="0"
                        title="Stok i ulët në paketa"
                        value={v.low_stock_packages}
                        onChange={(e) => setV(i, "low_stock_packages", e.target.value)}
                      />
                    </div>
                  ) : (
                    <Input
                      type="number"
                      min="0"
                      title="Stok i ulët"
                      value={v.low_stock_threshold}
                      onChange={(e) =>
                        setV(i, "low_stock_threshold", e.target.value)
                      }
                    />
                  )}
                  {form.variants.length > 1 && (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          variants: f.variants.filter((_, j) => j !== i),
                        }))
                      }
                    >
                      ×
                    </Button>
                  )}
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setForm((f) => ({ ...f, variants: [...f.variants, variant()] }))
              }
            >
              <Plus className="h-4 w-4 mr-1" />
              Shto model
            </Button>
            <Textarea
              placeholder="Shënime"
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
            />
            <DialogFooter>
              <Button>Ruaj</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={lowStockOpen} onOpenChange={setLowStockOpen}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-4xl max-h-[92vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Lista e produkteve për furnizim</DialogTitle>
          </DialogHeader>
          {!lowStockGroups.length ? (
            <p className="py-8 text-center text-emerald-700">Nuk ka produkte me stok të ulët.</p>
          ) : (
            <div className="space-y-5">
              {lowStockGroups.map((group) => (
                <section key={group.category}>
                  <h3 className="mb-2 rounded-md bg-slate-900 px-3 py-2 font-bold text-white">
                    {group.category} — {group.rows.length} modele
                  </h3>
                  <div className="overflow-x-auto rounded-md border">
                    <table className="w-full min-w-[620px] text-sm">
                      <thead className="bg-slate-50 text-left">
                        <tr>
                          <th className="p-3">Produkti</th>
                          <th>Modeli</th>
                          <th>SKU</th>
                          <th>Gjendja</th>
                          <th>Pragu i ulët</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.rows.map(({ product, variant }) => (
                          <tr className="border-t" key={`${product.id}-${variant.id}`}>
                            <td className="p-3 font-medium">{product.name}</td>
                            <td>{variant.model}</td>
                            <td>{product.sku || "—"}</td>
                            <td className="font-bold text-red-600">{stockText(product, variant)}</td>
                            <td>{thresholdText(product, variant)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={userOpen} onOpenChange={setUserOpen}>
        <DialogContent className="w-[calc(100vw-1rem)] max-h-[92vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? "Ndrysho shitësin" : "Shto shitës"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveUser} className="space-y-3">
            <Label>Username</Label>
            <Input
              required
              value={userForm.username}
              onChange={(e) =>
                setUserForm({ ...userForm, username: e.target.value })
              }
            />
            <Label>
              {editingUser
                ? "Fjalëkalim i ri (lëre bosh pa ndryshim)"
                : "Fjalëkalimi"}
            </Label>
            <Input
              type="password"
              required={!editingUser}
              value={userForm.password}
              onChange={(e) =>
                setUserForm({ ...userForm, password: e.target.value })
              }
            />
            <label className="flex gap-2">
              <input
                type="checkbox"
                checked={userForm.active}
                onChange={(e) =>
                  setUserForm({ ...userForm, active: e.target.checked })
                }
              />
              Aktiv
            </label>
            <DialogFooter>
              <Button>Ruaj</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={credOpen} onOpenChange={setCredOpen}>
        <DialogContent className="w-[calc(100vw-1rem)] max-h-[92vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Ndrysho kredencialet e Admin-it</DialogTitle>
          </DialogHeader>
          <form onSubmit={changeCreds} className="space-y-3">
            <Label>Fjalëkalimi aktual *</Label>
            <Input
              required
              type="password"
              value={creds.current_password}
              onChange={(e) =>
                setCreds({ ...creds, current_password: e.target.value })
              }
            />
            <Label>Username i ri</Label>
            <Input
              value={creds.new_username}
              onChange={(e) =>
                setCreds({ ...creds, new_username: e.target.value })
              }
            />
            <Label>Fjalëkalimi i ri</Label>
            <Input
              type="password"
              value={creds.new_password}
              onChange={(e) =>
                setCreds({ ...creds, new_password: e.target.value })
              }
            />
            <DialogFooter>
              <Button>Ndrysho</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
function Pagination({ page, pages, onChange }) {
  if (pages <= 1) return null;
  return (
    <div className="flex justify-center items-center gap-3 mt-5">
      <Button
        variant="outline"
        disabled={page <= 1}
        onClick={() => {
          onChange(page - 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      >
        Faqja e mëparshme
      </Button>
      <span className="text-sm font-semibold">
        Faqja {page} nga {pages}
      </span>
      <Button
        variant="outline"
        disabled={page >= pages}
        onClick={() => {
          onChange(page + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }}
      >
        Faqja tjetër
      </Button>
    </div>
  );
}
